import { apiServices } from '_services';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Lock, EyeOff } from 'react-feather';
import ReactTimeago from "react-timeago";

import './css/cosmetics.css';

import ShardsIcon from './css/images/shards.png';
import HourglassGif from './css/images/hourglass.gif';

import { RotateCcw } from 'react-feather';
import { CheckCircle } from 'react-feather';
import { Trash } from 'react-feather';
import { X } from 'react-feather';
import { Clock } from 'react-feather';
import { ArrowUp } from 'react-feather';
import { financesActions } from '_actions';

const FILTERABLE_SLOTS = ['HEAD', 'BODY', 'WEAPON'];

const StarIcon = ({ style = {}, ...props }) => <img {...props} alt=" " style={{ marginLeft: 4, ...style }} src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAYAAAAf8/9hAAAAuklEQVQ4EaWSQQ7CMAwE/aD8lM/wD25c8hBOnIx2m00cKw2oVIpcgmfWTWu2v9zMsC5d7q+Hey2XJX8JDrhNgEm+PYoaWDk6YAlq4X2TxF6ezUgTFCtgLe3X4u87Dxcy448pNTR2WJIGP29DMCShaQViD8kZ1js+JtlIdnCXwK50AEr9BZ4eRQCq1tnoU3psBoClPdSdpDcK1HvPktVH1ZMSPE0nEWqWMP0EXkqygAeokUUsqpL5BeL/D3HSduvowavxAAAAAElFTkSuQmCC" />;

function getDifferences(old, current) {
    if (!old || !current) {
        return [];
    }

    const allChanges = [];

    for (const c of old) {
        const match = current.find(e => e.id === c.id);

        if (match) {
            const changes = [];

            for (const key in c) {
                if (key === 'config') {
                    continue;
                }

                if (c[key] !== match[key]) {
                    changes.push({
                        property: key,
                        old: c[key],
                        current: match[key],
                    });
                }
            }

            if (changes.length) {
                allChanges.push({
                    id: c.id,
                    changes,
                });
            }
        }
    }

    return allChanges;
}

function sorter(a, b, sortBy, revenue) {
    if (sortBy === 'revenue' && revenue) {
        const revA = revenue.find(r => r.type === a.id);
        const revB = revenue.find(r => r.type === b.id);
        return (revB ? (revB.profit | 0) : 0) - (revA ? (revA.profit | 0) : 0);
    }

    return (b.priority * 1000 + b.id) - (a.priority * 1000 + a.id);
}

const Cosmetics = ({ finances, dispatch }) => {
    const [cosmetics, setCosmetics] = useState(null);
    const [originalCosmetics, setOriginalCosmetics] = useState(null);
    const [cosmeticFiles, setCosmeticFiles] = useState(null);
    // const [cosmeticSheets, setCosmeticSheets] = useState(null);
    const [selected, setSelected] = useState([]);
    const [charBase, setCharBase] = useState(null);
    const [rendered, setRendered] = useState(0);
    const [busy, setBusy] = useState(true);
    const [filter, setFilter] = useState("");
    const [sortBy, setSortBy] = useState('priority');

    const revenue = finances ? finances.topCosmetics : null;
    const lifetime = finances && finances.sumLT ? finances.sumLT.revenue : 0;

    const refreshRevenue = () => dispatch(financesActions.updateFinances());

    useEffect(() => {
        refreshRevenue();
    }, []);

    useEffect(() => {
        const key = window.setInterval(refreshRevenue, 10000);
        return () => window.clearInterval(key);
    }, []);

    useEffect(() => {
        (async () => {
            const [cosmetics, cosmeticFiles, cosmeticSheets, charBase] = await Promise.all([
                apiServices.axios.get('/data/cosmetics').then(r => r.data),
                apiServices.axios.get('/assets/get/config/cosmetics').then(r => r.data),
                apiServices.axios.get('/assets/get/characters/player/cosmetics').then(r => r.data),
                loadImage("characters/player/base.png"),
            ]);

            const remapped = Object.fromEntries(Object.entries(cosmeticFiles).map(e => JSON.parse(atob(e[1]))).map(e => [e.type, e]));

            // Used in the counter
            setCosmeticFiles(remapped);
            // setCosmeticSheets(cosmeticSheets);
            const count = cosmetics.length;

            setRendered(`0/${count}`);

            let jobs = [];

            // Pre-render cosmetics
            let n = 0;
            for (const c of cosmetics) {
                const config = remapped[c.id];

                if (config && config.sheet) {
                    const backgroundURL = c.preview_background ? `sprites/${c.preview_background}.png` : "sprites/ui/custom-cosmetic-preview.png";

                    const job = renderCosmetic(backgroundURL, charBase, config.sheet, c.slot, cosmeticSheets);
                    jobs.push(job);
                }

                n++;

                if (n % 50 === 0) {
                    setRendered(`${n}/${count}`);
                    await (Promise.all(jobs));

                    jobs.length = 0;
                }
            }

            await (Promise.all(jobs));
            jobs.length = 0;

            setOriginalCosmetics(cosmetics);
            setCosmetics(cosmetics);
            setCharBase(charBase);
            setBusy(false);
        })();
    }, []);

    const enriched = useMemo(() =>
        cosmetics && cosmetics
            .map(c => ({ ...c, config: cosmeticFiles[c.id] }))
            .sort((a, b) => sorter(a, b, sortBy, revenue)),
        [cosmetics, cosmeticFiles, sortBy, revenue]);

    const modified = useMemo(() => getDifferences(originalCosmetics, cosmetics), [originalCosmetics, cosmetics]);

    const modifyCosmetic = useCallback((id, key, value) => {
        const obj = cosmetics.find(e => e.id === id);

        if (obj) {
            const newObj = { ...obj, [key]: value };
            setCosmetics(list => list.map(e => e.id === id ? newObj : e));
        }
    }, [cosmetics, originalCosmetics]);

    const revertChanges = useCallback((id) => {
        const orig = originalCosmetics.find(e => e.id === id);

        if (orig) {
            const newObj = { ...orig };
            setCosmetics(list => list.map(e => e.id === id ? newObj : e));
        }
    }, [cosmetics, originalCosmetics]);

    const commitChanges = useCallback(() => {
        const payload = modified.map(m => ({
            id: m.id,
            properties: Object.fromEntries(m.changes.map(change => [change.property, change.current])),
        }));

        if (!window.confirm(JSON.stringify(payload, null, 2))) {
            return;
        }

        setBusy(true);

        apiServices.axios
            .post('/data/cosmetics/modify', payload)
            .then(r => r.data)
            .then(r => window.alert(JSON.stringify(r)))
            .then(() => apiServices.axios.get('/data/cosmetics')
                .then(r => {
                    setOriginalCosmetics(r.data);
                    setCosmetics(r.data);
                }))
            .finally(() => setBusy(false));
    }, [modified]);

    if (cosmetics === null || charBase == null) {
        return <p>Rendering... {rendered}</p>
    }

    let filtered = enriched;

    if (filter) {
        if (filter === 'unreleased') {
            filtered = enriched.filter(c => c.developer_only);
        } else if (filter === 'released') {
            filtered = enriched.filter(c => !c.developer_only);
        } else if (filter === 'featured') {
            filtered = enriched.filter(c => c.featured);
        } else {
            filtered = enriched.filter(c => c.slot === filter);
        }
    }

    let ltv = 0;
    if (revenue) {
        ltv = revenue.map(v => v.profit).reduce((prev, cur) => prev + cur, 0);
    }

    let selectedProfit = 0;
    if (revenue) {
        selectedProfit = selected
            .map(type => revenue.find(c => c.type === type))
            .filter(v => v).map(v => v.profit)
            .reduce((prev, cur) => prev + cur, 0);
    }

    return (
        <div className='cosmetics-container'>
            <PageHeader cosmetics={enriched} slotFilter={filter} setSlotFilter={setFilter} selected={selected} />
            <ModificationOverview modified={modified} cosmetics={cosmetics} modifyCosmetic={modifyCosmetic} revertChanges={revertChanges} commitChanges={commitChanges} />
            <BulkSelectionOverview cosmetics={enriched} modifyCosmetic={modifyCosmetic} selected={selected} charBase={charBase} setOriginalCosmetics={setOriginalCosmetics} clearSelection={() => setSelected([])} />

            Sort result by:
            <select className='form-control form-control-sm' value={sortBy} onChange={ev => setSortBy(ev.currentTarget.value)} style={{ width: 150 }}>
                <option value="revenue">Profit (lifetime)</option>
                <option value="priority">Priority</option>
                <option value="id">Internal ID</option>
            </select>

            <h3>Total sales: $ {ltv | 0} ({(ltv / 1000) | 0} K USD)</h3>
            <h3>Total sales (all types): $ {lifetime | 0} ({(lifetime / 1000) | 0} K USD)</h3>

            <h3>Total sales (selected): $ {selectedProfit | 0} ({(selectedProfit / 1000) | 0} K USD)</h3>

            <hr />

            <CosmeticsGrid cosmetics={filtered} selected={selected} setSelected={setSelected} charBase={charBase} modifyCosmetic={modifyCosmetic} revenue={revenue ? revenue : []} />
        </div>
    )
}

const PageHeader = ({ cosmetics, slotFilter, setSlotFilter, selected }) => {
    return (
        <div className='cosmetics-header'>
            <h1>Cosmetics <h5>{selected.length ? selected.length : cosmetics.length}/{cosmetics.length} cosmetics</h5></h1>

            <select className='form-control form-control-sm' value={slotFilter} onChange={ev => setSlotFilter(ev.currentTarget.value)} style={{ width: 120 }}>
                <option value="">Show all</option>

                <option disabled>-- SLOTS --</option>
                <option value="HEAD">Heads</option>
                <option value="OUTFIT">Outfits</option>
                <option value="WEAPON">Weapons</option>

                <option disabled>-- FILTERING --</option>
                <option value="unreleased">Unreleased</option>
                <option value="released">Released</option>
                <option value="featured">Featured</option>
            </select>
        </div>
    );
}

const BulkSelectionOverview = ({ cosmetics, modifyCosmetic, selected, clearSelection, charBase, setOriginalCosmetics }) => {
    if (!cosmetics.length) {
        return null;
    }

    const [expanded, setExpanded] = useState(true);
    const [fomoTimer, setFomoTimer] = useState("48");
    const [releaseAfter, setReleaseAfter] = useState("0");
    const [bumpPriority, setBumpPriority] = useState(true);
    const [makeFeatured, setMakeFeatured] = useState(true);
    const [busy, setBusy] = useState(false);

    const selectedCosmetics = selected.map(id => cosmetics.find(cos => cos.id === id));

    const autorelease = async () => {
        if (!window.confirm('Are you sure you want to auto-release these?')) {
            return;
        }

        if (busy) {
            return;
        }

        setBusy(true);

        const payload = {
            ids: selected,
            fomoTimer: Number(fomoTimer),
            releaseAfter: Number(releaseAfter),
            bumpPriority,
            makeFeatured,
        };

        apiServices.axios
            .post('/data/cosmetics/autorelease', payload)
            .then(() => apiServices.axios.get('/data/cosmetics'))
            .then(r => setOriginalCosmetics(r.cosmetics))
            .finally(() => setBusy(false));
    };

    return (
        <div className='modification-overview'>
            <div className='modifications-header'>
                <div className='header-title' onClick={() => setExpanded(v => !v)}>
                    Bulk actions ({selected.length})
                </div>

                <button className='btn btn-xs btn-secondary' disabled={!selected.length} onClick={clearSelection}>
                    <X />&nbsp;&nbsp;Clear selection
                </button>
            </div>

            {!selected.length ? <span style={{ padding: 8 }}>Nothing selected.</span> : expanded ? <>
                <div className='bulk-action-wrapper' style={{ padding: 8 }}>
                    Release these <b>{selected.length}</b> cosmetics

                    <select value={releaseAfter} onChange={e => setReleaseAfter(e.currentTarget.value)} style={{ margin: '0 3px' }} className='form-control form-control-xs d-inline w-auto mx-2'>
                        <option value="0">NOW</option>
                        <option value="6">in 6h (silhouette)</option>
                        <option value="12">in 12h (silhouette)</option>
                        <option value="24">in 1 day (silhouette)</option>
                        <option value="48">in 2 days (silhouette)</option>
                        <option value="72">in 3 days (silhouette)</option>
                        <option value="168">in 7 days (silhouette)</option>
                    </select>

                    with a FOMO timer of

                    <select value={fomoTimer} onChange={e => setFomoTimer(e.currentTarget.value)} style={{ margin: '0 3px' }} className='form-control form-control-xs d-inline w-auto mx-2'>
                        <option value="0">none</option>
                        <option value="24">1 day (24h)</option>
                        <option value="48">2 days (48h)</option>
                        <option value="72">3 days (72h)</option>
                    </select>

                    {releaseAfter !== "0" && Number(fomoTimer) > 0 && <span> after going live</span>}

                    and <label className='form-control form-control-xs d-inline w-auto mx-2'><input type="checkbox" checked={bumpPriority} onChange={e => setBumpPriority(e.currentTarget.checked)} /> bump them to the top</label>,
                    and <label className='form-control form-control-xs d-inline w-auto mx-2'><input type="checkbox" checked={makeFeatured} onChange={e => setMakeFeatured(e.currentTarget.checked)} /> set them as featured</label>.

                    <button className='btn btn-xs btn-primary' onClick={autorelease} disabled={busy}>
                        <CheckCircle />&nbsp;&nbsp;Execute
                    </button>
                </div>

                <div style={{ padding: 8 }}>
                    Bulk actions apply to the following cosmetics:
                </div>

                <div className='bulk-selection-body'>
                    <div className='cosmetics-grid'>
                        {selectedCosmetics.map(e =>
                            <CosmeticEntry
                                key={e.id}
                                data={e}
                                isSelected={true}
                                setSelected={v => { }}
                                modifyCosmetic={modifyCosmetic}
                                charBase={charBase}
                            />
                        )}
                    </div>
                </div>
            </> : null}

        </div>
    )
}

const ModificationOverview = ({ modified, cosmetics, modifyCosmetic, revertChanges, commitChanges }) => {
    return (
        <div className='modification-overview'>
            <div className='modifications-header'>
                <div className='header-title'>
                    Pending Changes ({modified.length})
                </div>

                <button className='btn btn-xs btn-primary' onClick={commitChanges} disabled={!modified.length}>
                    <CheckCircle />&nbsp;&nbsp;Commit
                </button>
            </div>

            <div className='modified-cosmetics'>
                {!modified.length && <span>There are no changes pending commit.</span>}

                {modified.map(e => <ModifiedCosmetic
                    key={e.id}
                    data={e}
                    cosmetic={cosmetics.find(v => v.id === e.id)}
                    modifyCosmetic={modifyCosmetic}
                    revertChanges={revertChanges}
                />)}
            </div>
        </div>
    )
}

const ModifiedCosmetic = ({ data, cosmetic, modifyCosmetic, revertChanges }) => {
    return (
        <div className='modified-cosmetic'>
            <div className='modified-cosmetic-header'>
                <span>{cosmetic.name} #{data.id} ({data.changes.length}x)</span>

                <span className='header-extra'>
                    <span className='delete-button' onClick={() => window.confirm("Are you sure you want to revert this cosmetic?") && revertChanges(data.id)}>
                        <Trash size={15} />
                    </span>
                </span>
            </div>

            <div className='modified-cosmetic-delta'>
                {data.changes.map(c => (
                    <div className='modified-property' key={c.property}>
                        <div>
                            <span className='property-name'>{c.property}</span> modified from <Tag>{`${c.old}`}</Tag> to <Tag>{`${c.current}`}</Tag>
                        </div>

                        <span className='delete-button' onClick={() => modifyCosmetic(data.id, c.property, c.old)}>
                            <RotateCcw size={15} />
                        </span>
                    </div>
                ))}
            </div>
        </div>
    )
}

const CosmeticsGrid = ({ cosmetics, selected, setSelected, charBase, modifyCosmetic, revenue }) => {
    const toggleSelection = (id, v) => {
        setSelected(l => v ?
            [...l, id] :
            l.filter(i => i !== id));
    }

    return (
        <div className='cosmetics-grid'>
            {cosmetics.map(e =>
                <CosmeticEntry
                    key={e.id}
                    data={e}
                    isSelected={selected.includes(e.id)}
                    setSelected={v => toggleSelection(e.id, v)}
                    modifyCosmetic={modifyCosmetic}
                    charBase={charBase}
                    revenue={revenue.find(r => r.type === e.id)}
                />
            )}
        </div>
    );
}

const CosmeticEntry = ({ data, isSelected, setSelected, charBase, modifyCosmetic, revenue }) => {
    const {
        id,
        name,
        developer_only,
        price,
        slot,
        full_body,
        unobtainable,
        hidden,
        overview_background,
        preview_background,
        created_at,
        updated_at,
        updated_by_character,
        priority,
        members_only,
        lone_wolf,
        projectile_override_id,
        featured,
        config,
        unobtainable_after_epoch,
        released_after_epoch,
    } = data;

    const classes = ['cosmetics-grid-entry'];
    if (isSelected) {
        classes.push('selected');
    }

    const rendered = useCosmetic(
        preview_background ? `sprites/${preview_background}.png` : "sprites/ui/custom-cosmetic-preview.png",
        charBase,
        (config && config.sheet) ? config.sheet : null
    );

    const changePrice = () => {
        const newPrice = window.prompt("Enter shards cost:", price);
        if (newPrice && newPrice !== `${price}`) {
            const v = Number.parseInt(newPrice);
            if (Number.isNaN(v) || v < 1) {
                window.alert("Invalid number: " + newPrice);
            } else {
                modifyCosmetic(id, "price", v | 0);
            }
        }
    };

    const changeName = () => {
        const newName = window.prompt("Enter name:", name);

        if (newName && newName !== name) {
            modifyCosmetic(id, "name", newName.trim());
        }
    };

    const grayed = hidden;
    const unobtainableYeah = (unobtainable_after_epoch && unobtainable_after_epoch < (Date.now() / 1000));
    const prelaunch = (released_after_epoch && released_after_epoch > (Date.now() / 1000));
    const fomoUpcoming = (unobtainable_after_epoch && unobtainable_after_epoch > (Date.now() / 1000));

    let filter = '';

    if (grayed) {
        filter = 'grayscale(0.95)';
    }

    if (unobtainableYeah) {
        filter += ' brightness(0.5)';
    } else if (developer_only) {
        filter += ' brightness(0.65)';
    }

    let profit = null;
    if (revenue && !developer_only) {
        if (revenue.profit >= 1000) {
            const k = (revenue.profit / 1000) | 0;
            const rem = (revenue.profit - (k * 1000)) | 0;
            const padded = String(rem).padStart(3, '0');
            profit = `${k},${padded}`;
        } else {
            profit = `${revenue.profit | 0}`;
        }
    }

    return (<div className={classes.join(' ')}>
        <div className='cosmetic-item-header'>
            <div className='cosmetic-name'>
                <span onClick={changeName} style={{ textDecoration: hidden ? "line-through" : "none" }}>{name}</span>
            </div>

            {profit && <span className='cosmetic-profit'>
                $&nbsp;{profit}
            </span>}

            {/* <span className='cosmetic-id'>#{id}</span> */}
        </div>

        <div className='cosmetic-item-subheader'>
            {hidden && <EyeOff size={14} />}
            {developer_only && <Lock size={14} />}

            <input className='selection-checkbox' type="checkbox" checked={isSelected} onChange={ev => setSelected(ev.currentTarget.checked)} />
        </div>

        <div className='cosmetic-body' style={{ position: 'relative' }}>
            <img draggable={false} src={rendered} alt="" class="cosmetic-preview" style={{ filter: filter ? filter : 'none' }} onClick={() => setSelected(!isSelected)} />

            {(prelaunch || fomoUpcoming) &&
                <img src={HourglassGif} className="cosmetic-hourglass" alt="" />
            }

            <div className='tag-wrapper'>
                {hidden && <Tag color="red"><EyeOff size={14} /> Hidden</Tag>}
                {developer_only && <Tag color="blue"><Clock size={14} /> Unreleased</Tag>}
                {prelaunch && <Tag color="blue"><Clock size={14} /> Launching</Tag>}
                {unobtainable && <Tag color="red">Unobtainable</Tag>}
                {unobtainableYeah && <Tag><Clock size={14} /> Expired</Tag>}
            </div>


            {prelaunch ?
                <Tag color="blue" className='cosmetic-countdown'>
                    <img src={HourglassGif} className='pixelated' alt="" />
                    <Countdown millis={released_after_epoch * 1000} />
                </Tag>
                : fomoUpcoming &&
                <Tag color="yellow" className='cosmetic-countdown'>
                    <img src={HourglassGif} className='pixelated' alt="" />
                    <Countdown millis={unobtainable_after_epoch * 1000} />
                </Tag>
            }

            <div className='shards-cost' onClick={changePrice}>
                <img src={ShardsIcon} className='shards-icon' alt="" />&nbsp;{price}
            </div>

            <StarIcon
                className="featured-star"
                style={{ filter: featured ? 'none' : 'grayscale(1) brightness(0.75)' }}
                onClick={() => modifyCosmetic(id, "featured", !featured)} />
        </div>
    </div>);
}

function formatCountdown(millis) {
    let seconds = ((millis - Date.now()) / 1000) | 0;
    const h = (seconds / 3600) | 0;
    seconds %= 3600;
    const m = (seconds / 60) | 0;
    seconds %= 60;

    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

const Countdown = ({ millis, ...rest }) => {
    const [fmt, setFmt] = useState(formatCountdown(millis));

    useEffect(() => {
        const key = setInterval(() => setFmt(formatCountdown(millis)), 1000);
        return () => clearInterval(key);
    }, [millis]);

    return (
        <span {...rest}>{fmt}</span>
    )
}

const makeKey = (backgroundURL, cosmeticURL) => `${backgroundURL}|${cosmeticURL}`;
const imageCache = new Map();

function useCosmetic(backgroundURL, charBase, cosmeticURL) {
    return imageCache.get(makeKey(backgroundURL, cosmeticURL));
}


function makeAssetUrl(path) {
    return `${apiServices.baseURL}/assets/get/${path}?token=${apiServices.currentToken()}`;
}

// Load all images as ImageBitmaps (compatible with OffscreenCanvas)
const loadImage = (url) => fetch(makeAssetUrl(url))
    .then(res => res.blob())
    .then(blob => createImageBitmap(blob));

async function renderCosmetic(backgroundURL, charBase, cosmeticURL, slot, characterFiles) {
    const key = makeKey(backgroundURL, cosmeticURL);

    if (imageCache.has(key)) {
        return imageCache.get(key);
    }

    const canvas = new OffscreenCanvas(51, 50);
    const ctx = canvas.getContext('2d');

    // const sheetData = Uint8Array.fromBase64(characterFiles[cosmeticURL]);
    const data = characterFiles[cosmeticURL];
    const sheetData = data ? Uint8Array.fromBase64(data) : null;

    let [background, cosmetic] = await Promise.allSettled([
        imageCache.has(backgroundURL) ? Promise.resolve(imageCache.get(backgroundURL)) : loadImage(backgroundURL),
        sheetData ? createImageBitmap(new Blob([sheetData], { type: 'image/png' })) : Promise.resolve(null),
    ]).then(p => p.map(r => r.value));

    if (!background) {
        backgroundURL = "sprites/ui/custom-cosmetic-preview.png";
        background = await (imageCache.has(backgroundURL) ? Promise.resolve(imageCache.get(backgroundURL)) : loadImage(backgroundURL));
    }

    if (background && !imageCache.has(backgroundURL)) {
        imageCache.set(backgroundURL, background);
    }

    // Render background at dest position 13, 11
    if (background) {
        ctx.drawImage(background, 0, 0);
    }

    const offsetX = -7;
    const offsetY = -6;

    // Render player base - first frame (source 0,0 64x64 → dest 0,0 64x64)
    if (slot === 'WEAPON') {
        ctx.drawImage(charBase, 0, 64 * 3, 64, 64, offsetX, offsetY, 64, 64);
    } else {
        ctx.drawImage(charBase, 0, 0, 64, 64, offsetX, offsetY, 64, 64);
    }

    // Render cosmetic - first frame (source 0,0 64x64 → dest 0,0 64x64)
    if (cosmetic) {
        if (slot === 'WEAPON') {
            ctx.drawImage(cosmetic, 0, 64 * 3, 64, 64, offsetX, offsetY, 64, 64);
        } else {
            ctx.drawImage(cosmetic, 0, 0, 64, 64, offsetX, offsetY, 64, 64);
        }

        // Clean up ImageBitmaps
        cosmetic.close();
    }

    const blob = await canvas.convertToBlob({ type: 'image/png' });
    const v = URL.createObjectURL(blob);

    imageCache.set(key, v);
    return v;
}

const Tag = ({ children = [], color = "", className = "" }) => {
    return <span className={'colored-tag ' + color + " " + className}>{children}</span>
}


function mapStateToProps(state) {
    return { finances: state.finances };
}

const connected = connect(mapStateToProps)(Cosmetics);
export { connected as Cosmetics };
